import { useEffect, useState } from "react";
import api, { qs } from "@/lib/api";
import { SVC_KINDS, fmtINR, fmtDay } from "@/lib/service";

const TH = "px-3 py-2 text-left text-[11px] uppercase tracking-wider font-semibold text-muted-foreground whitespace-nowrap";

function TodayDots({ kinds }) {
  const has = new Set(kinds);
  return (
    <div className="flex items-center gap-1">
      {SVC_KINDS.map((k) => (
        <span key={k.key} title={k.label} className={`inline-flex h-4 w-4 items-center justify-center rounded-sm text-[9px] font-bold ${has.has(k.key) ? "bg-emerald-500 text-white" : "bg-secondary text-muted-foreground"}`}>{k.short}</span>
      ))}
    </div>
  );
}

export function ServiceCoverage({ month }) {
  const [cov, setCov] = useState(null);
  useEffect(() => {
    api.get(`/service/coverage?${qs({ month })}`).then(({ data }) => setCov(data)).catch(() => setCov(null));
  }, [month]);
  if (!cov) return null;
  const t = cov.totals;
  return (
    <div className="border bg-card rounded-md overflow-auto" data-testid="svc-coverage">
      <div className="px-4 py-3 border-b flex items-center gap-3"><h3 className="font-heading text-sm font-semibold">Data coverage by workshop</h3><span className="text-[11px] text-muted-foreground">reporting today {t.reporting_today}/{cov.workshops}</span></div>
      <table className="w-full text-xs">
        <thead className="border-b">
          <tr>{["Workshop", "Zone", "Today's files", "ROs invoiced", "Invoices", "Lines", "Revenue ₹", "RO list (open / total)", "Warranty claims", "Warranty ₹", "Sales units", "Data up to"].map((h) => <th key={h} className={TH}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {cov.rows.map((r) => (
            <tr key={r.code} className="border-b last:border-0 hover:bg-muted/50" data-testid={`svc-coverage-${r.code}`}>
              <td className="px-3 py-1.5 font-medium whitespace-nowrap">{r.code} <span className="text-muted-foreground font-normal">{r.name}</span></td>
              <td className="px-3 py-1.5 text-muted-foreground">{r.zone || "—"}</td>
              <td className="px-3 py-1.5"><TodayDots kinds={r.today_kinds} /></td>
              <td className="px-3 py-1.5 num font-semibold">{fmtINR(r.ros_invoiced)}</td>
              <td className="px-3 py-1.5 num">{fmtINR(r.invoices)}</td>
              <td className="px-3 py-1.5 num text-muted-foreground">{fmtINR(r.gst_lines)}</td>
              <td className="px-3 py-1.5 num font-semibold">{fmtINR(r.revenue)}</td>
              <td className="px-3 py-1.5 num"><span className={r.ro_open ? "text-amber-700 font-semibold" : ""}>{r.ro_open}</span> <span className="text-muted-foreground">/ {r.ro_total}</span></td>
              <td className="px-3 py-1.5 num">{r.warranty_claims}</td>
              <td className="px-3 py-1.5 num" data-testid={`svc-coverage-wty-${r.code}`}>{fmtINR(r.warranty_revenue)}</td>
              <td className="px-3 py-1.5 num">{r.sales_units}</td>
              <td className="px-3 py-1.5 text-muted-foreground whitespace-nowrap">{fmtDay(r.data_to)}</td>
            </tr>
          ))}
          {cov.rows.length === 0 && <tr><td colSpan={12} className="px-3 py-6 text-center text-muted-foreground">No workshops in your scope — ask the admin to assign outlets to your account</td></tr>}
        </tbody>
        {cov.rows.length > 1 && (
          <tfoot className="border-t bg-secondary/40 font-semibold">
            <tr data-testid="svc-coverage-total">
              <td className="px-3 py-1.5" colSpan={3}>Group · {cov.workshops} workshops</td>
              <td className="px-3 py-1.5 num">{fmtINR(t.ros_invoiced)}</td>
              <td className="px-3 py-1.5 num">{fmtINR(t.invoices)}</td>
              <td className="px-3 py-1.5 num">{fmtINR(t.gst_lines)}</td>
              <td className="px-3 py-1.5 num">{fmtINR(t.revenue)}</td>
              <td className="px-3 py-1.5 num">{t.ro_open} / {t.ro_total}</td>
              <td className="px-3 py-1.5 num">{t.warranty_claims}</td>
              <td className="px-3 py-1.5 num">{fmtINR(t.warranty_revenue)}</td>
              <td className="px-3 py-1.5 num">{t.sales_units}</td>
              <td />
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
